import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Meta, Title } from '@angular/platform-browser';
import { environment } from 'src/environments/environment';
import { BehaviorSubject, Observable, defer, throwError } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';
import { whitelabelCompanyName, whitelabelIsByk, whitelabelLogo } from '../helpers/whitelabel-theme';

// how long the full-screen loader stays up after the details arrive
const DETAILS_LOADER_HOLD_MS = 2000;
// page title on Biyaheko's own site (index.html itself starts neutral)
const BYK_TITLE = 'Biyaheko | B2B Travel Platform for Filipino Travel Agents';
// Biyaheko's own organisation: shown with the Biyaheko title, not its account name
const BYK_ORG_NAMES = ['biyaheko master'];

/** Head tags (description, share previews, organization data) on Biyaheko's own site. */
const BYK_HEAD = {
  description: 'Biyaheko is the B2B travel platform for Filipino travel agents since 2012. Tours, visas, hotels, Super POS, BK Academy training and support in one ecosystem. Become a Biyaheko partner.',
  siteName: 'Biyaheko',
  ogTitle: 'Biyaheko | Start selling the world',
  ogDescription: 'The B2B travel platform for Filipino travel agents. Operating since 2012.',
  url: 'https://biyaheko.com/home',
  image: 'https://biyaheko.com/assets/biyaheko-landing/og-image.png',
  ldJson: {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization', '@id': 'https://biyaheko.com/#org', name: 'Biyaheko',
        alternateName: ['Biyaheko.com', 'BiyaheKo'], url: 'https://biyaheko.com/home',
        logo: 'https://biyaheko.com/assets/biyaheko-landing/logo-square.png', foundingDate: '2012',
        description: 'B2B travel platform for Filipino travel agents: tours, visas, hotels, Super POS and training, operating since 2012.',
        areaServed: 'PH',
        sameAs: ['https://www.facebook.com/OfficialBiyaheKo', 'https://www.instagram.com/biyaheko.ph_official/',
          'https://www.threads.com/@biyaheko.ph_official', 'https://www.tiktok.com/@officialbiyaheko.ph',
          'https://www.linkedin.com/company/biyaheko-ph/'],
      },
      {
        '@type': 'WebSite', '@id': 'https://biyaheko.com/#website', name: 'Biyaheko', alternateName: 'Biyaheko.com',
        url: 'https://biyaheko.com/home', publisher: { '@id': 'https://biyaheko.com/#org' }, inLanguage: 'en-PH',
      },
    ],
  },
};

/** General wording for any organisation's site, with its name filled in (no product
 *  list: organisations don't all offer the same products). */
const orgDescription = (name: string) => `${name} is a B2B travel platform for travel agents.`;

@Injectable({
  providedIn: 'root'
})
export class WhitelabelService {

  private whitelabelDetails$?: Observable<any>;
  private detailsLoading = new BehaviorSubject<boolean>(false);
  // true while the whitelabel details are being fetched (drives the full-screen loader)
  readonly detailsLoading$ = this.detailsLoading.asObservable();

  constructor(private http: HttpClient, private title: Title, private meta: Meta, @Inject(DOCUMENT) private document: Document) { }

  // Website colours and details for the pre-login pages (landing, login, register).
  // Fetched once and shared; a failed call is dropped so the next page retries it.
  // The loader shows for the request itself and stays 2s after a success; a failure
  // drops it straight away so the "Unauthorised access" page shows.
  getWhitelabelDetails(): Observable<any> {
    if (!this.whitelabelDetails$) {
      this.whitelabelDetails$ = defer(() => {
        this.detailsLoading.next(true);
        return this.http.get(`${environment.apiUrl}organization/whitelabel/details`);
      }).pipe(
        tap((details) => {
          setTimeout(() => this.detailsLoading.next(false), DETAILS_LOADER_HOLD_MS);
          this.applyDetailsBranding(details);
        }),
        catchError((error) => {
          this.detailsLoading.next(false);
          this.whitelabelDetails$ = undefined;
          return throwError(() => error);
        }),
        shareReplay(1)
      );
    }
    return this.whitelabelDetails$;
  }

  // ---- tab title, description, share previews ------------------------------
  // index.html starts neutral (no company name) so no brand shows while things load;
  // these fill in the organisation (or Biyaheko). The favicon is always the default.

  /** Before login: an organisation's site gets its own name, Biyaheko's own site its
   *  own texts. Also re-run by the login page, since the details are cached and a
   *  logout would otherwise keep the last organisation's branding. */
  applyDetailsBranding(details: any) {
    if (whitelabelIsByk(details)) this.applyBrand(null);
    else this.applyBrand(whitelabelCompanyName(details), whitelabelLogo(details));
  }

  /** After login: the signed-in organisation (the stored `organization`: its
   *  organization_name and profile_picture). Called on sign-in and on app start. */
  applyOrganizationBranding(org: any) {
    const name = typeof org?.organization_name === 'string' ? org.organization_name.trim() : '';
    if (name) this.applyBrand(name, whitelabelLogo({ organization: org }));
  }

  /** Title + head tags for an organisation; null (or Biyaheko's own, "Biyaheko Master")
   *  gets Biyaheko's. */
  private applyBrand(name: string | null, logo: string | null = null) {
    if (!name || BYK_ORG_NAMES.includes(name.trim().toLowerCase())) {
      this.title.setTitle(BYK_TITLE);
      this.setHead(BYK_HEAD.description, BYK_HEAD.siteName, BYK_HEAD.ogTitle, BYK_HEAD.ogDescription,
        BYK_HEAD.image, BYK_HEAD.url, BYK_HEAD.ldJson);
      return;
    }
    const description = orgDescription(name);
    const origin = this.document.location?.origin || '';
    this.title.setTitle(name);
    this.setHead(description, name, `${name} | Agent Portal`, description, logo, null, {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name,
      ...(origin ? { url: origin } : {}),
      ...(logo ? { logo } : {}),
      description,
    });
  }

  /** description, og:* / twitter:* tags and the JSON-LD block; a null image or url drops that tag. */
  private setHead(description: string, siteName: string, ogTitle: string, ogDescription: string,
    image: string | null, url: string | null, ldJson: object) {
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ property: 'og:site_name', content: siteName });
    this.meta.updateTag({ property: 'og:title', content: ogTitle });
    this.meta.updateTag({ property: 'og:description', content: ogDescription });
    if (url) this.meta.updateTag({ property: 'og:url', content: url });
    else this.meta.removeTag(`property='og:url'`);
    if (image) {
      this.meta.updateTag({ property: 'og:image', content: image });
      this.meta.updateTag({ name: 'twitter:image', content: image });
      this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    } else {
      this.meta.removeTag(`property='og:image'`);
      this.meta.removeTag(`name='twitter:image'`);
      this.meta.updateTag({ name: 'twitter:card', content: 'summary' });
    }
    // og-image.png is 1200x630; other images' sizes are unknown
    if (image === BYK_HEAD.image) {
      this.meta.updateTag({ property: 'og:image:width', content: '1200' });
      this.meta.updateTag({ property: 'og:image:height', content: '630' });
    } else {
      this.meta.removeTag(`property='og:image:width'`);
      this.meta.removeTag(`property='og:image:height'`);
    }
    let script = this.document.getElementById('brand-ld-json') as HTMLScriptElement | null;
    if (!script) {
      script = this.document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'brand-ld-json';
      this.document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(ldJson);
  }

  getWhitelabelPages() {
    return this.http.get<any[]>(`${environment.apiUrl}organization/whitelabel`);
  }

  postWhitelabelPage(user) {
    return this.http.post(`${environment.apiUrl}organization/whitelabel`, user);
  }

  updateWhitelabelPage(user) {
    return this.http.patch(`${environment.apiUrl}organization/whitelabel`, user);
  }

  deletePage(id) {
    return this.http.delete(`${environment.apiUrl}organization/whitelabel/${id}/`);
  }

  getWLStatus() {
    return this.http.get<any[]>(`${environment.apiUrl}organization/whitelabel/status`);
  }

  requestWLAccess(data) {
    return this.http.patch(`${environment.apiUrl}organization/whitelabel/change/status`, data);
  }

  getWLHost() {
    return this.http.get<any[]>(`${environment.apiUrl}organization/whitelabel/host`);
  }

  setWLHost(data) {
    return this.http.post(`${environment.apiUrl}organization/whitelabel/host`, data);
  }

  getWhitelabelList(params: any): Observable<any> {
    return this.http.get(`${environment.apiUrl}organization/whitelabel/change/status`, { params });
  }

  changeAccess(data: any) {
    return this.http.patch(`${environment.apiUrl}organization/whitelabel/change/status`, data);
  }

  getPageContent(url) {
    return this.http.post(`${environment.apiUrl}organization/cms/content`, url);
  }

}
